import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import PageHeader from '../components/PageHeader';
import Spinner from '../components/Spinner';
import { useProjectHistory } from '../features/settings/hooks/useProjectHistory';
import { PROJECT_HISTORY_LABELS as LABELS } from '../utils/labels';
import type { HistoryAction, HistoryEntityType } from '../types/projectHistory';

interface ProjectHistoryPageProps {
  orgId: string | null;
}

interface ProjectHistoryLocationState {
  projectId: string;
  projectName: string;
}

const SEARCH_DEBOUNCE_MS = 300;

// Which actions actually occur for each entity type — matches the logProjectHistory call
// sites in useManageProjects.ts (project/address) and useMachines.ts (machine/booking).
// Drives which "Actie" options are offered once a specific "Type" is selected.
const ACTIONS_BY_ENTITY_TYPE: Record<HistoryEntityType, HistoryAction[]> = {
  project: ['create', 'update', 'archive', 'unarchive'],
  address: ['create', 'update', 'delete'],
  machine: ['create', 'update', 'delete'],
  booking: ['create', 'update', 'delete', 'pickup_confirmed', 'handin_confirmed'],
};

const ALL_ACTIONS: HistoryAction[] = ['create', 'update', 'delete', 'archive', 'unarchive', 'pickup_confirmed', 'handin_confirmed'];

const ACTION_OPTIONS: { value: HistoryAction; label: keyof typeof LABELS; testId: string }[] = [
  { value: 'create', label: 'actionCreate', testId: 'option-history-action-create' },
  { value: 'update', label: 'actionUpdate', testId: 'option-history-action-update' },
  { value: 'delete', label: 'actionDelete', testId: 'option-history-action-delete' },
  { value: 'archive', label: 'actionArchive', testId: 'option-history-action-archive' },
  { value: 'unarchive', label: 'actionUnarchive', testId: 'option-history-action-unarchive' },
  { value: 'pickup_confirmed', label: 'actionPickupConfirmed', testId: 'option-history-action-pickup-confirmed' },
  { value: 'handin_confirmed', label: 'actionHandinConfirmed', testId: 'option-history-action-handin-confirmed' },
];

const ENTITY_TYPE_LABEL_KEYS: Record<HistoryEntityType, keyof typeof LABELS> = {
  project: 'entityProject',
  address: 'entityAddress',
  machine: 'entityMachine',
  booking: 'entityBooking',
};

export default function ProjectHistoryPage({ orgId }: ProjectHistoryPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ProjectHistoryLocationState | null;
  const projectId = state?.projectId ?? null;

  const [entityType, setEntityType] = useState<HistoryEntityType | 'all'>('all');
  const [action, setAction] = useState<HistoryAction | 'all'>('all');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounced so filtering doesn't fire a query on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const availableActions = entityType === 'all' ? ALL_ACTIONS : ACTIONS_BY_ENTITY_TYPE[entityType];

  const handleEntityTypeChange = (nextEntityType: HistoryEntityType | 'all') => {
    setEntityType(nextEntityType);
    const nextAvailableActions = nextEntityType === 'all' ? ALL_ACTIONS : ACTIONS_BY_ENTITY_TYPE[nextEntityType];
    if (action !== 'all' && !nextAvailableActions.includes(action)) {
      setAction('all');
    }
  };

  const hasActiveFilters = entityType !== 'all' || action !== 'all' || !!startDate || !!endDate || !!search;

  const filterSummaryParts: string[] = [];
  if (entityType !== 'all') {
    filterSummaryParts.push(`${LABELS.entityTypeLabel}: ${LABELS[ENTITY_TYPE_LABEL_KEYS[entityType]]}`);
  }
  if (action !== 'all') {
    const selectedAction = ACTION_OPTIONS.find((opt) => opt.value === action);
    if (selectedAction) filterSummaryParts.push(`${LABELS.actionLabel}: ${LABELS[selectedAction.label]}`);
  }
  if (startDate) filterSummaryParts.push(`${LABELS.startDateLabel}: ${startDate.format('DD/MM/YYYY')}`);
  if (endDate) filterSummaryParts.push(`${LABELS.endDateLabel}: ${endDate.format('DD/MM/YYYY')}`);
  if (search) filterSummaryParts.push(`${LABELS.searchLabel}: "${search}"`);

  const { entries, loading, error, hasMore, loadMore } = useProjectHistory(orgId, projectId, {
    entityType,
    action,
    startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
    endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
    search,
  });

  if (!state?.projectId) return <Navigate to="/projects" replace />;

  const handleReset = () => {
    setEntityType('all');
    setAction('all');
    setStartDate(null);
    setEndDate(null);
    setSearchInput('');
    setSearch('');
  };

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/projects')}
              data-testid="back-button"
            >
              {LABELS.back}
            </Button>
            <h1 className="text-xl font-medium mt-2">
              {LABELS.title} - {state.projectName}
            </h1>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <FormControl size="small" className="min-w-36">
              <InputLabel id="history-entity-type-label">{LABELS.entityTypeLabel}</InputLabel>
              <Select
                labelId="history-entity-type-label"
                label={LABELS.entityTypeLabel}
                value={entityType}
                onChange={(e: SelectChangeEvent) => handleEntityTypeChange(e.target.value as HistoryEntityType | 'all')}
                inputProps={{ 'aria-label': LABELS.entityTypeLabel }}
                data-testid="history-entity-type-filter"
              >
                <MenuItem value="all" data-testid="option-history-entity-all">{LABELS.entityAll}</MenuItem>
                <MenuItem value="project" data-testid="option-history-entity-project">{LABELS.entityProject}</MenuItem>
                <MenuItem value="address" data-testid="option-history-entity-address">{LABELS.entityAddress}</MenuItem>
                <MenuItem value="machine" data-testid="option-history-entity-machine">{LABELS.entityMachine}</MenuItem>
                <MenuItem value="booking" data-testid="option-history-entity-booking">{LABELS.entityBooking}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" className="min-w-36">
              <InputLabel id="history-action-label">{LABELS.actionLabel}</InputLabel>
              <Select
                labelId="history-action-label"
                label={LABELS.actionLabel}
                value={action}
                onChange={(e: SelectChangeEvent) => setAction(e.target.value as HistoryAction | 'all')}
                inputProps={{ 'aria-label': LABELS.actionLabel }}
                data-testid="history-action-filter"
              >
                <MenuItem value="all" data-testid="option-history-action-all">{LABELS.actionAll}</MenuItem>
                {ACTION_OPTIONS.filter((opt) => availableActions.includes(opt.value)).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} data-testid={opt.testId}>
                    {LABELS[opt.label]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DatePicker
              label={LABELS.startDateLabel}
              value={startDate}
              onChange={setStartDate}
              maxDate={endDate ?? dayjs()}
              slotProps={{ textField: { size: 'small', className: 'w-36' } }}
            />
            <DatePicker
              label={LABELS.endDateLabel}
              value={endDate}
              onChange={setEndDate}
              minDate={startDate ?? undefined}
              maxDate={dayjs()}
              slotProps={{ textField: { size: 'small', className: 'w-36' } }}
            />

            <TextField
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              label={LABELS.searchLabel}
              placeholder={LABELS.searchPlaceholder}
              data-testid="history-search-filter"
            />

          </div>

          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/25 bg-surface px-4 py-3">
              <FilterAltIcon color="primary" fontSize="small" />
              <div className="flex flex-col min-w-0">
                <span className="text-xs uppercase tracking-wide text-text-secondary">{LABELS.filterActive}</span>
                <span className="text-sm font-medium truncate">{filterSummaryParts.join(' · ')}</span>
              </div>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={handleReset}
                data-testid="history-reset-filters-button"
                className="ml-auto shrink-0"
              >
                {LABELS.resetFilters}
              </Button>
            </div>
          )}

          {error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={loadMore}>
                  {LABELS.retry}
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            {entries.length === 0 && !loading ? (
              <p className="text-sm text-text-secondary text-center py-6">
                {hasActiveFilters ? LABELS.noResults : LABELS.noEntries}
              </p>
            ) : (
              entries.map((entry, i) => (
                <div key={entry.id}>
                  {i > 0 && <hr className="border-border-subtle" />}
                  <div className="flex flex-col gap-1 px-4 py-3">
                    <span className="text-xs text-text-disabled">
                      {dayjs(entry.created_at).format('D MMMM YYYY, HH:mm')} - {entry.actor_name}
                    </span>
                    <span className="text-sm">{entry.description}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {loading && (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          )}

          {hasMore && !loading && (
            <div className="flex justify-center">
              <Button onClick={loadMore} data-testid="load-more-button">
                {LABELS.loadMore}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
